import { getValidAccessToken } from '../../lib/supabase';
import { sendInstagramMessage } from './instagramService';
import { acceptChatSendResponse, recoverChatSendReceipt } from './chatSendReceipt';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Extrai IGSID do remote_jid (formato ig:{igsid}).
 */
export function parseInstagramIgsid(remoteJid: string): string | null {
    if (!remoteJid.startsWith('ig:')) return null;
    const id = remoteJid.slice(3).trim();
    return id || null;
}

async function postInstagramMultipart(
    fields: Record<string, string>,
    file: File,
    fallback: string,
    onAccepted?: () => void,
): Promise<{ messageId?: string; mediaUrl?: string; captionError?: string }> {
    if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuração do Supabase ausente.');
    }

    const token = await getValidAccessToken();
    const formData = new FormData();
    for (const [key, value] of Object.entries(fields)) {
        formData.append(key, value);
    }
    formData.append('file', file);
    const requestId = fields.requestId || crypto.randomUUID();
    if (!fields.requestId) formData.append('requestId', requestId);

    let response: Response;
    try {
    response = await fetch(`${supabaseUrl}/functions/v1/instagram-api`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            apikey: supabaseAnonKey,
        },
        body: formData,
    });
    } catch {
        return recoverChatSendReceipt(requestId, onAccepted);
    }
    if (response.status >= 500) return recoverChatSendReceipt(requestId, onAccepted);

    const data = await response.json().catch(() => ({})) as { error?: string; messageId?: string; mediaUrl?: string };
    if (!response.ok || data.error) {
        throw new Error(data.error || fallback);
    }
    return acceptChatSendResponse(data, requestId, onAccepted);
}

export async function sendInstagramTextMessage(params: {
    requestId?: string;
    onAccepted?: () => void;
    organizationId: string;
    integrationId: string;
    remoteJid: string;
    text: string;
}): Promise<{ messageId?: string }> {
    const igsid = parseInstagramIgsid(params.remoteJid);
    if (!igsid) {
        throw new Error('Conversa Instagram inválida (IGSID ausente)');
    }

    return sendInstagramMessage({
        organizationId: params.organizationId,
        integrationId: params.integrationId,
        recipientIgsid: igsid,
        text: params.text,
        requestId: params.requestId, onAccepted: params.onAccepted,
    });
}

export async function sendInstagramMediaMessage(params: {
    requestId?: string;
    onAccepted?: () => void;
    organizationId: string;
    integrationId: string;
    remoteJid: string;
    attachmentType: 'audio' | 'image' | 'video' | 'document';
    file: File;
    /** Enviado como texto logo após a mídia (API IG não tem caption no attachment). */
    caption?: string;
}): Promise<{ messageId?: string; mediaUrl?: string; captionError?: string }> {
    const igsid = parseInstagramIgsid(params.remoteJid);
    if (!igsid) {
        throw new Error('Conversa Instagram inválida (IGSID ausente)');
    }

    const fields: Record<string, string> = {
        action: 'send-media-message',
        organizationId: params.organizationId,
        integrationId: params.integrationId,
        recipientIgsid: igsid,
        attachmentType: params.attachmentType,
        ...(params.requestId ? { requestId: params.requestId } : {}),
    };
    const caption = params.caption?.trim();
    if (caption && params.attachmentType !== 'audio') {
        fields.caption = caption;
    }

    return postInstagramMultipart(fields, params.file, 'Erro ao enviar mídia Instagram', params.onAccepted);
}
